import { useState } from "react";
import { ChevronDown, ChevronUp, SlidersHorizontal, Trash2 } from "lucide-react";
import type { PairSimProfile } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import type { CompatibilityRow } from "@/types/line-editor.types";
import { PairProfileFields } from "./PairProfileFields";

interface Props {
  compat: CompatibilityRow;
  productName: string;
  canEdit: boolean;
  pending: boolean;
  onRemove: () => void;
  onSaveProfile: (next: PairSimProfile | null) => void;
}

export function CompatibilityItem({
  compat,
  productName,
  canEdit,
  pending,
  onRemove,
  onSaveProfile,
}: Props) {
  const [open, setOpen] = useState(false);
  const hasProfile = Boolean(compat.simProfile && Object.keys(compat.simProfile).length > 0);

  return (
    <li className="rounded border px-2 py-1.5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{productName}</p>
          <div className="mt-0.5 flex items-center gap-1.5">
            <Pill tone={compat.enabled ? "success" : "neutral"} withDot>
              {compat.enabled ? "Habilitada" : "Desabilitada"}
            </Pill>
            {hasProfile ? <Pill tone="info">perfil próprio</Pill> : null}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            aria-label={`${open ? "Ocultar" : canEdit ? "Editar" : "Ver"} parâmetros de simulação de ${productName}`}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <SlidersHorizontal className="h-3.5 w-3.5" />
            )}
          </Button>
          {canEdit ? (
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Remover compatibilidade com ${productName}`}
              onClick={onRemove}
              disabled={pending}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          ) : null}
        </div>
      </div>

      {open ? (
        <div className="mt-2 space-y-2 border-t pt-2">
          <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
            <ChevronDown className="h-3 w-3" />
            Parâmetros de simulação neste produto
          </p>
          <PairProfileFields
            profile={compat.simProfile}
            readOnly={!canEdit}
            saving={pending}
            onSave={onSaveProfile}
          />
        </div>
      ) : null}
    </li>
  );
}
